import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
      // Attach role (default to null so we can trigger role-selector modal on first login)
      const u = res.data;
      u.role = u.role || null;
      setUser(u);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const setRole = async (role) => {
    const res = await axios.post(`${API}/user/role`, { role }, { withCredentials: true });
    setUser(res.data.user);
    return res.data.user;
  };

  useEffect(() => {
    // CRITICAL: skip /auth/me if returning from OAuth callback — AuthCallback will handle it
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = () => {
    window.location.href = '/login?next=' + encodeURIComponent(window.location.pathname);
  };

  const loginWithGoogle = (next) => {
    const dest = next || new URLSearchParams(window.location.search).get('next') || '/';
    if (dest.startsWith('/') && !dest.startsWith('/login')) sessionStorage.setItem('post_login_next', dest);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const loginWithPassword = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    const u = res.data.user; u.role = u.role || null;
    setUser(u);
    return u;
  };

  const register = async (name, email, password) => {
    const res = await axios.post(`${API}/auth/register`, { name, email, password }, { withCredentials: true });
    const u = res.data.user; u.role = u.role || null;
    setUser(u);
    return u;
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch (e) { console.warn('logout request failed', e); }
    setUser(null);
    window.location.href = '/';
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithGoogle, loginWithPassword, register, logout, refresh: checkAuth, setUser, setRole }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
